/**
 * Live status plumbing for the Azure DevOps area — a THIN SELECTOR over the
 * shared realtime bus (`src/realtime/`).
 *
 * The API publishes Azure DevOps webhook activity (build status changes,
 * work-item updates) as ONE Feathers custom service event,
 * `app.service('devops-pipelines').emit('devops-event', envelope)`. That event
 * is parsed exactly once, by `realtime/channels.ts`'s {@link devopsChannel},
 * into the canonical {@link DevOpsRealtimeEvent} — which carries the untouched
 * envelope, the ported headline, AND the flat grid projection.
 *
 * This module hands the PipelinesBoard / WorkItems stores the flat projection
 * they have always consumed. `views/home/homeLive.ts` selects the envelope from
 * the same canonical event for the dashboard feed. There is no longer a second
 * parser: the leaner consumer selects from the richer shape.
 *
 * CONNECTION: none is opened here. The bus owns exactly one socket for the
 * whole app (normally the raw socket under the Feathers client, which is
 * already authenticated and already on the `internal` staff channel), refcounts
 * it in one place, and tears the listeners down when the last subscriber
 * leaves — so navigating in and out of the DevOps views cannot leak sockets.
 *
 * AUTH: sourced internally by the bus from the AuthStore. There is deliberately
 * no token parameter, so the old "subscribed without a token, got silence"
 * failure cannot be expressed.
 *
 * SECRETS: only the JWT bearer authenticates the socket. No PAT / webhook
 * secret is ever read, sent, or received here — the event payloads carry status
 * metadata only (ids, enum codes, branch names).
 *
 * IF THE STREAM IS DOWN: the bus records it on `realtimeDiagnostics` and
 * console.errors it (never a silent no-op), and the stores keep working
 * pull-only via the header Refresh button / post-action re-pulls. See the
 * freshness policy in `realtime/freshnessPolicy.ts`.
 */
import type RootStore from "stores/RootStore";
import {
  DEVOPS_EVENT_WIRE,
  devopsChannel,
  subscribeRealtime,
} from "realtime";
import type { DevOpsEvent } from "./devops.types";

/**
 * The Socket.io wire event name — re-exported from the canonical channel so
 * this area keeps its historical constant without owning a second definition.
 */
export const DEVOPS_EVENT = DEVOPS_EVENT_WIRE;

type DevOpsEventHandler = (event: DevOpsEvent) => void;

/**
 * Subscribe to live `devops-event` activity, flattened for the grids. Returns
 * an idempotent disposer; the bus drops the underlying listeners when the last
 * subscriber across the app leaves.
 *
 * Envelopes whose `type` maps to no grid entity (`flat === null`) are skipped
 * here — exactly as before — while the dashboard still receives them.
 *
 * @param rootStore used by the bus to resolve the shared connection and the
 *   AuthStore JWT. The caller cannot supply a token.
 */
export function subscribeDevOpsEvents(
  rootStore: RootStore,
  handler: DevOpsEventHandler
): () => void {
  return subscribeRealtime(rootStore, devopsChannel, (event) => {
    if (!event.flat) return;
    handler(event.flat);
  });
}
